/**
 * projectTabs.ts — kateri zasloni sestavljajo projekt (T1474)
 * ===========================================================
 *
 * Projekt ima tri enakovredne zaslone: **Popis · Nadzorna plošča · Nastavitve**.
 * Do T1474 sta obstajala samo prva dva, preklop med njima pa je bil asimetričen
 * — iz popisa je vodil gumb, nazaj pa samo drobtina s šifro projekta (in če se
 * `code` ni naložil, poti nazaj sploh ni bilo).
 *
 * **Zakaj tabela in ne `pathname.endsWith('boq')` po kodi.** Aktivni zavihek
 * potrebujeta vrstica zavihkov IN layout, ki iz njega izpelje način postavitve.
 * Aplikacija teče na deklarativnih `<Routes>` — od T2487 kot potomci ene poti
 * `*` podatkovnega usmerjevalnika (`main.tsx`) —, zato `useMatches()` in
 * `handle` zaslonov projekta ne vidita — ta modul je
 * njun nadomestek in edino mesto, kjer piše, kateri zasloni sploh obstajajo.
 *
 * **Prevodi namerno niso v `editor.*` ali `navigation.*`:** ta namespacea
 * generira modulni manifest (`apps/boq_builder/manifest/locales/*.json` prek
 * `collect_frontend_locales`), zato bi jih naslednji `sync_modules` povozil.
 */

export type ProjectTabKey = 'boq' | 'dashboard' | 'settings';

export interface ProjectTab {
  key: ProjectTabKey;
  /** Zadnji segment poti pod `/projects/:id/`. */
  segment: string;
  /** i18n ključ oznake. Nikoli v namespace, ki ga lasti manifest. */
  labelKey: string;
  /**
   * Ali zaslon drsenje prevzame nase.
   *
   * Popis je `h-full … overflow-hidden` in si drsenje ureja sam; nadzorna
   * plošča in nastavitve sta navadna vsebina, ki drsi v `<main>` iz
   * `DashboardLayout`. Če bi layout `overflow-hidden` vsilil vsem, bi nadzorno
   * ploščo odrezal — zato je to lastnost zavihka, ne domneva postavitve.
   */
  fullBleed: boolean;
}

export const PROJECT_TABS: readonly ProjectTab[] = [
  { key: 'boq', segment: 'boq', labelKey: 'projects.tab_boq', fullBleed: true },
  {
    key: 'dashboard',
    segment: 'dashboard',
    labelKey: 'projectDashboard.title',
    fullBleed: false,
  },
  {
    key: 'settings',
    segment: 'settings',
    labelKey: 'projects.tab_settings',
    fullBleed: false,
  },
] as const;

/** Pot do zavihka danega projekta. */
export function projectTabPath(projectId: number | string, tab: ProjectTabKey): string {
  const match = PROJECT_TABS.find((t) => t.key === tab);
  return `/projects/${projectId}/${match?.segment ?? tab}`;
}

/**
 * Aktivni zavihek iz poti, ali `null`, kadar pot ni zavihek projekta.
 *
 * `null` je pomemben izid, ne pomanjkljivost: IFC preslikava
 * (`/projects/:projectId/ifc/:uploadId/mapping`) je celozaslonski čarovnik in
 * **sestrska** ruta — bere `:projectId`, ne `:id`. Če bi jo tabela priznala, bi
 * glava obljubila preklop, ki ga ruta ne pozna. Enako velja za neznan segment:
 * privzetek bi označil zavihek, ki ni prikazan.
 */
export function resolveProjectTab(pathname: string): ProjectTabKey | null {
  const parts = pathname.split('/').filter(Boolean);
  // Pričakovana oblika je natanko `projects / <id> / <segment>`; karkoli
  // globljega (IFC) ali plitvejšega zavihek ni.
  if (parts.length !== 3 || parts[0] !== 'projects') return null;
  return PROJECT_TABS.find((tab) => tab.segment === parts[2])?.key ?? null;
}

/** Ali zaslon na tej poti drsenje prevzame nase (gl. `ProjectTab.fullBleed`). */
export function isFullBleedPath(pathname: string): boolean {
  const key = resolveProjectTab(pathname);
  return PROJECT_TABS.find((tab) => tab.key === key)?.fullBleed ?? false;
}

/**
 * Ali je pot urejevalnik popisa (T2410).
 *
 * Urejevalnik potrebuje vso širino za tabelo (maketa A): na njem je stranska
 * vrstica privzeto skrčena na ikone in glava projekta je ena sama vrstica
 * skupaj z globalno zgornjo vrstico. Merilo je isti zavihek kot pri
 * `isFullBleedPath`, zato IFC preslikava (sestrska ruta) ni urejevalnik.
 */
export function isBoqEditorPath(pathname: string): boolean {
  return resolveProjectTab(pathname) === 'boq';
}
